import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { request } from '../api/client'
import type { HuntFilters } from '../api/queries'

const WINDOWS = [1, 6, 24, 24 * 7, 24 * 30]

function label(hours: number): string {
  if (hours < 24) return `last ${hours} h`
  return `last ${hours / 24} day${hours === 24 ? '' : 's'}`
}

/**
 * Pinning a hunt to the dashboard.
 *
 * The window is asked for explicitly and cannot be inherited from the search, even
 * though the search has one. A hunt is run once against a range somebody chose; a tile
 * is asked again every time the page opens, and a tile carrying its author's Tuesday
 * afternoon would report that Tuesday every morning for a month while looking live.
 * So the form asks "how far back should this look, from now" and the answer is a
 * duration.
 */
export function PinToDashboard({
  filters,
  groupBy,
  canPin,
  onDone,
}: {
  filters: HuntFilters
  groupBy: string
  canPin: boolean
  onDone: () => void
}) {
  const [title, setTitle] = useState('')
  const [hours, setHours] = useState(24)
  const client = useQueryClient()

  const pin = useMutation({
    mutationFn: () =>
      request<{ id: string }>('/dashboard/tiles/', {
        method: 'POST',
        body: {
          title: title.trim(),
          window_hours: hours,
          group_by: groupBy,
          filters: {
            q: filters.q ?? '',
            hostname: filters.hostname ?? '',
            user: filters.user ?? '',
            src_ip: filters.src_ip ?? '',
          },
        },
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dashboard-tiles'] })
      onDone()
    },
  })

  return (
    <div className="panel" role="group" aria-labelledby="pin-heading" style={{ marginBottom: 'var(--space-3)' }}>
      <h3 id="pin-heading" style={{ marginTop: 0, fontSize: 'var(--text-md)' }}>
        Pin this search to the dashboard
      </h3>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'end' }}>
        <label>
          <span className="muted">Title</span>
          <input
            aria-label="Tile title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <label>
          <span className="muted">Looking back, from now</span>
          <select
            aria-label="Window"
            value={hours}
            onChange={(event) => setHours(Number(event.target.value))}
          >
            {WINDOWS.map((option) => (
              <option key={option} value={option}>
                {label(option)}
              </option>
            ))}
          </select>
        </label>
        <button
          className="primary"
          disabled={!canPin || !title.trim() || pin.isPending}
          onClick={() => pin.mutate()}
        >
          Pin it
        </button>
        <button onClick={onDone}>Cancel</button>
      </div>

      <p className="muted" style={{ marginBottom: 0 }}>
        The tile follows now. It will not keep the range this search ran over — a panel that
        reports the same numbers every morning is worse than no panel.
      </p>

      {pin.isError && (
        <p className="error" role="alert" style={{ marginBottom: 0 }}>
          {(pin.error as Error).message}
        </p>
      )}
      {!canPin && (
        <p className="muted" style={{ marginBottom: 0 }}>
          Your role reads but does not change what the shift sees, so this cannot be pinned from
          here.
        </p>
      )}
    </div>
  )
}
